import { useEffect, useRef, useState } from "react";
import {
    Image as ImageIcon, Upload, Eye, EyeOff, Trash2, Loader2, CheckCircle2,
    AlertTriangle, History, RefreshCw, Camera, Crop, X,
} from "lucide-react";
import { api } from "../../lib/api";

const ASSET_KEY = "founder_photo";
const ASSET_TITLE = "Founder Photo — Mr. Anand Rajput";
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ALLOWED_EXT_LABEL = "JPG · JPEG · PNG · WebP · max 5 MB";

/**
 * Admin → Photo Management (Super-Admin only)
 *
 * Features (per requirement spec):
 *   ✓ Upload new photo
 *   ✓ Replace existing
 *   ✓ Hide/Show toggle
 *   ✓ Delete (falls back to default avatar everywhere)
 *   ✓ Live client-side preview (FileReader) before saving
 *   ✓ Server-side square crop + WebP resize on save
 *   ✓ JPG / JPEG / PNG / WebP support
 *   ✓ 5 MB file-size cap
 *   ✓ MongoDB-backed storage — survives redeploys, no code changes needed
 *   ✓ Super-Admin gated (403 surfaced as inline message)
 *   ✓ Full audit log table (newest first)
 */
export default function PhotoManagementPanel() {
    const [meta, setMeta] = useState(null);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [forbidden, setForbidden] = useState(false);

    // Upload flow state
    const [pickedFile, setPickedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [pickedError, setPickedError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [actionBusy, setActionBusy] = useState("");
    const [flash, setFlash] = useState("");
    const fileInputRef = useRef(null);

    const refresh = async () => {
        try {
            const [m, a] = await Promise.all([
                api.get(`/site-assets/${ASSET_KEY}`),
                api.get(`/admin/site-assets/${ASSET_KEY}/audit`).catch((e) => {
                    if (e?.response?.status === 403) setForbidden(true);
                    return { data: [] };
                }),
            ]);
            setMeta(m.data);
            setAudit(a.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [m, a] = await Promise.all([
                    api.get(`/site-assets/${ASSET_KEY}`),
                    api.get(`/admin/site-assets/${ASSET_KEY}/audit`).catch((e) => {
                        if (e?.response?.status === 403 && !cancelled) setForbidden(true);
                        return { data: [] };
                    }),
                ]);
                if (cancelled) return;
                setMeta(m.data);
                setAudit(a.data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // -------- File selection / preview --------
    const onPick = (file) => {
        setPickedError("");
        setFlash("");
        if (!file) { setPickedFile(null); setPreviewUrl(null); return; }
        if (!ALLOWED_MIME.includes(file.type)) {
            setPickedError(`Unsupported format. Accepted: ${ALLOWED_EXT_LABEL}`);
            setPickedFile(null);
            setPreviewUrl(null);
            return;
        }
        if (file.size > MAX_BYTES) {
            setPickedError(`File is too large (${(file.size / 1048576).toFixed(2)} MB). Max 5 MB.`);
            setPickedFile(null);
            setPreviewUrl(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target.result);
        reader.readAsDataURL(file);
        setPickedFile(file);
    };

    const onFileChange = (e) => onPick(e.target.files?.[0] || null);
    const onDrop = (e) => {
        e.preventDefault();
        onPick(e.dataTransfer.files?.[0] || null);
    };
    const onDragOver = (e) => e.preventDefault();

    const clearPicked = () => {
        setPickedFile(null);
        setPreviewUrl(null);
        setPickedError("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // -------- Save / upload --------
    const onUpload = async () => {
        if (!pickedFile) return;
        setUploading(true);
        setFlash("");
        try {
            // V55 fix — use axios `api` instance so the request gets the
            // X-Super-Mode-Token header from the request interceptor (raw
            // fetch() bypasses it, which caused a false 403 "Super-Admin
            // access required" right after the upload completed).
            const fd = new FormData();
            fd.append("file", pickedFile);
            await api.post(`/admin/site-assets/${ASSET_KEY}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setFlash("Photo updated and live across the website.");
            clearPicked();
            await refresh();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 403) {
                // Distinguish missing super-mode token vs real RBAC denial:
                // - "super_mode_required"  → re-unlock super mode (NOT a real auth issue)
                // - any other 403 message → genuine RBAC denial
                if (detail === "super_mode_required") {
                    setPickedError("Super Mode has expired — please re-enter your 6-digit MPIN to continue.");
                } else {
                    setForbidden(true);
                    setPickedError("Super-Admin access required");
                }
            } else {
                setPickedError(detail || e.message || "Upload failed");
            }
        } finally {
            setUploading(false);
        }
    };

    const onToggleVisibility = async () => {
        if (!meta?.exists) return;
        setActionBusy("toggle");
        setFlash("");
        try {
            await api.patch(`/admin/site-assets/${ASSET_KEY}`, { visible: !meta.visible });
            setFlash(meta.visible ? "Photo hidden — default avatar now showing." : "Photo restored — visible across the website.");
            await refresh();
        } catch (e) {
            setPickedError(e?.response?.data?.detail || "Toggle failed");
            if (e?.response?.status === 403) setForbidden(true);
        } finally {
            setActionBusy("");
        }
    };

    const onDelete = async () => {
        if (!meta?.exists) return;
        if (!window.confirm("Delete the founder photo? The site will fall back to the default letter avatar until you upload a new one.")) return;
        setActionBusy("delete");
        setFlash("");
        try {
            await api.delete(`/admin/site-assets/${ASSET_KEY}`);
            setFlash("Photo deleted. Default avatar restored across the website.");
            await refresh();
        } catch (e) {
            setPickedError(e?.response?.data?.detail || "Delete failed");
            if (e?.response?.status === 403) setForbidden(true);
        } finally {
            setActionBusy("");
        }
    };

    if (loading) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center" data-testid="photo-mgmt-loading">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    if (forbidden) {
        return (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900" data-testid="photo-mgmt-forbidden">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600" />
                    <div>
                        <div className="font-bold mb-1">Super-Admin access required</div>
                        <p className="text-sm">Photo Management is restricted to Super-Admin accounts. If you need access, ask the site owner to grant your account the <code className="bg-amber-100 px-1 rounded">super_admin</code> role.</p>
                    </div>
                </div>
            </div>
        );
    }

    const liveUrl = meta?.exists && meta?.raw_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${meta.raw_url}` : null;

    return (
        <div className="space-y-6" data-testid="photo-management-panel">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] inline-flex items-center gap-2">
                        <Camera className="w-6 h-6 text-[#1E3A8A]" /> Photo Management
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{ASSET_TITLE} · Updates push live instantly · No code changes required.</p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg"
                    data-testid="photo-mgmt-refresh"
                >
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {flash && (
                <div data-testid="photo-mgmt-flash" className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> {flash}
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">

                {/* Current photo */}
                <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="photo-mgmt-current">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-4">Current Photo</div>
                    <div className="flex items-center gap-6">
                        <div className="shrink-0">
                            {liveUrl ? (
                                <img
                                    src={liveUrl}
                                    alt={ASSET_TITLE}
                                    width={144}
                                    height={144}
                                    data-testid="photo-mgmt-current-img"
                                    className="w-36 h-36 rounded-full object-cover ring-4 ring-white shadow-lg"
                                />
                            ) : (
                                <div data-testid="photo-mgmt-no-photo" className="w-36 h-36 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 ring-4 ring-white shadow-lg flex items-center justify-center">
                                    <ImageIcon className="w-12 h-12 text-blue-300" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Status</div>
                            {!meta?.exists ? (
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded">
                                    Default avatar (no photo uploaded)
                                </span>
                            ) : meta.visible ? (
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">
                                    <Eye className="w-3.5 h-3.5" /> Live on website
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded">
                                    <EyeOff className="w-3.5 h-3.5" /> Hidden (default avatar shown)
                                </span>
                            )}
                            {meta?.exists && (
                                <div className="mt-3 space-y-0.5 text-xs text-slate-500">
                                    <div>Version: <span className="font-semibold text-slate-700">v{meta.version}</span></div>
                                    <div>Size: <span className="font-semibold text-slate-700">{(meta.size_bytes / 1024).toFixed(1)} KB</span></div>
                                    <div>Format: <span className="font-semibold text-slate-700">WebP · 512×512</span></div>
                                    <div>Updated: <span className="font-semibold text-slate-700">{(meta.updated_at || "").slice(0, 19).replace("T", " ")} UTC</span></div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    {meta?.exists && (
                        <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={onToggleVisibility}
                                disabled={actionBusy === "toggle"}
                                data-testid="photo-mgmt-toggle"
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-60"
                            >
                                {actionBusy === "toggle"
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : (meta.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />)}
                                {meta.visible ? "Hide photo" : "Show photo"}
                            </button>
                            <button
                                type="button"
                                onClick={onDelete}
                                disabled={actionBusy === "delete"}
                                data-testid="photo-mgmt-delete"
                                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60"
                            >
                                {actionBusy === "delete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Delete photo
                            </button>
                        </div>
                    )}
                </section>

                {/* Upload */}
                <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="photo-mgmt-upload-card">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">{meta?.exists ? "Replace photo" : "Upload photo"}</div>
                    <p className="text-xs text-slate-500 mb-3">Server auto-crops to a perfect square + resizes to 512×512 WebP. {ALLOWED_EXT_LABEL}.</p>

                    {/* Drop zone */}
                    {!pickedFile ? (
                        <label
                            htmlFor="photo-mgmt-file"
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            data-testid="photo-mgmt-dropzone"
                            className="block border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-blue-50/40 cursor-pointer transition-colors"
                        >
                            <Upload className="w-9 h-9 mx-auto text-slate-400 mb-2" />
                            <div className="font-semibold text-slate-700 text-sm">Drop image here or click to choose</div>
                            <div className="text-xs text-slate-500 mt-1">{ALLOWED_EXT_LABEL}</div>
                            <input
                                ref={fileInputRef}
                                id="photo-mgmt-file"
                                type="file"
                                accept={ALLOWED_MIME.join(",")}
                                onChange={onFileChange}
                                className="hidden"
                                data-testid="photo-mgmt-file-input"
                            />
                        </label>
                    ) : (
                        <div className="space-y-3">
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1.5">
                                    <Crop className="w-3.5 h-3.5" /> Live Preview · circular crop (auto-square on save)
                                </div>
                                <div className="flex items-center gap-4">
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        data-testid="photo-mgmt-preview"
                                        className="w-28 h-28 rounded-full object-cover ring-4 ring-white shadow"
                                    />
                                    <div className="text-xs text-slate-600 space-y-0.5 min-w-0 flex-1">
                                        <div className="font-semibold text-slate-800 truncate">{pickedFile.name}</div>
                                        <div>{(pickedFile.size / 1048576).toFixed(2)} MB · {pickedFile.type.split("/")[1].toUpperCase()}</div>
                                        <div className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Validated</div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={onUpload}
                                    disabled={uploading}
                                    data-testid="photo-mgmt-save"
                                    className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                                >
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                    {uploading ? "Uploading…" : "Save as live photo"}
                                </button>
                                <button
                                    type="button"
                                    onClick={clearPicked}
                                    disabled={uploading}
                                    data-testid="photo-mgmt-cancel"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg bg-slate-100 hover:bg-slate-200"
                                >
                                    <X className="w-4 h-4" /> Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {pickedError && (
                        <div data-testid="photo-mgmt-error" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {pickedError}
                        </div>
                    )}
                </section>
            </div>

            {/* Audit log */}
            <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="photo-mgmt-audit">
                <div className="flex items-center gap-2 mb-4">
                    <History className="w-5 h-5 text-[#1E3A8A]" />
                    <h3 className="font-heading font-bold text-lg text-[#0F172A]">Audit Log</h3>
                    <span className="text-xs text-slate-500">({audit.length} {audit.length === 1 ? "entry" : "entries"})</span>
                </div>
                {audit.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4">No changes recorded yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                    <th className="py-2 pr-3">When</th>
                                    <th className="py-2 pr-3">Action</th>
                                    <th className="py-2 pr-3">By</th>
                                    <th className="py-2 pr-3">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audit.map((row) => (
                                    <tr key={row.id} data-testid={`photo-mgmt-audit-row-${row.id}`} className="border-b border-slate-100 last:border-0">
                                        <td className="py-2.5 pr-3 text-slate-700 whitespace-nowrap">{(row.created_at || "").slice(0, 19).replace("T", " ")}</td>
                                        <td className="py-2.5 pr-3">
                                            <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded ${
                                                row.action === "delete" ? "bg-red-50 text-red-700"
                                                : row.action === "toggle" ? "bg-amber-50 text-amber-700"
                                                : row.action === "seed" ? "bg-slate-100 text-slate-700"
                                                : "bg-emerald-50 text-emerald-700"
                                            }`}>{row.action}</span>
                                        </td>
                                        <td className="py-2.5 pr-3 text-slate-600">{row.actor_email || "—"}</td>
                                        <td className="py-2.5 pr-3 text-slate-500 text-xs">
                                            {row.meta?.version && <div>version: v{row.meta.version}</div>}
                                            {row.meta?.visible !== undefined && <div>visible: {String(row.meta.visible)}</div>}
                                            {row.meta?.original_filename && <div>file: {row.meta.original_filename}</div>}
                                            {row.meta?.processed_size_bytes && <div>processed: {(row.meta.processed_size_bytes / 1024).toFixed(1)} KB</div>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
